import { useCallback } from 'react';

import {
   PromiseVoidFunction,
   TranslatableText,
   useSingleInterpolatedTranslationKey,
   useTranslate,
} from '@dreryk/react-common';
import { ActionEventInterceptor, useLocationWithBackState, usePushBack } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';

import { appointmentsTranslationKeys, visitTranslationKeys } from '../../../../translations';
import { AppointmentWaitlistCreateActionEventInterceptor } from '../interceptors/AppointmentWaitlistCreateActionEventInterceptor';

import { AppointmentWaitlistCreateFormController } from './AppointmentWaitlistCreateForm.types';

const transformFirstLetterToLowerCase = (string: string): string =>
   string.charAt(0).toLocaleLowerCase() + string.slice(1);

type AppointmentWaitlistCreateBookActionRenderer = (renderProps: { label: TranslatableText }) => JSX.Element;

type AppointmentWaitlistCreateBookActionProps = Readonly<
   Pick<AppointmentWaitlistCreateFormController, 'handleSubmit'> & {
      children: AppointmentWaitlistCreateBookActionRenderer;
      onAppointmentTimeSlotOccupiedFault: VoidFunction;
      waitlistEntryId: number;
      onSuccess: (dateConfirmedWithPatient: boolean) => void;
   }
>;

export const AppointmentWaitlistCreateBookAction = (props: AppointmentWaitlistCreateBookActionProps): JSX.Element => {
   const pushBack = usePushBack();
   const location = useLocationWithBackState();
   const translate = useTranslate();
   const label = useSingleInterpolatedTranslationKey(
      appointmentsTranslationKeys.create.modal.confirm.registerAndGoBack,
      'label',
      transformFirstLetterToLowerCase(translate(location?.state.label ?? visitTranslationKeys.goBackToAppointmentsList))
   );

   const handleSuccess = useCallback(() => {
      pushBack(visitPaths.getAppointmentsListPath());
   }, [pushBack]);

   return (
      <AppointmentWaitlistCreateActionEventInterceptor
         handleSubmit={props.handleSubmit}
         onAppointmentTimeSlotOccupiedFault={props.onAppointmentTimeSlotOccupiedFault}
         waitlistEntryId={props.waitlistEntryId}
         onSuccess={props.onSuccess}
      >
         <ActionEventInterceptor action={handleSuccess as PromiseVoidFunction}>
            {props.children({ label: label })}
         </ActionEventInterceptor>
      </AppointmentWaitlistCreateActionEventInterceptor>
   );
};
